import { useState, useRef, useMemo, useEffect } from 'react'
import {
  FormControl,
  Input,
  Pressable,
  Text,
} from '@ayble-health/dashboard-components'
import { Button } from 'components/ui/button'
import { PressablePill } from 'components/ui/pressable-pill'
import type { MedicationKind } from '../../models/careOSPrototype'
import { useMedicationCatalog } from '../../api/careOSPrototype/useMedicationCatalog'

// Inline add panel for the Pharmacy tab. Mirrors the labs order composer
// (LabsTab → LabOrderFormInline + TestComposer): opens in place above the
// list rather than as a modal, filters a catalog as you type, supports
// keyboard nav, and accepts free text for anything not in the catalog.
//
// Differences from labs, by design: single-select (one med per add), no
// panels/presets, and a second required-ish Dose field.

export interface NewMedicationDraft {
  kind: MedicationKind
  name: string
  dose: string
  instructions: string
  notes: string
}

const KINDS: { value: MedicationKind; label: string }[] = [
  { value: 'medication', label: 'Medication' },
  { value: 'supplement', label: 'Supplement' },
]

export default function AddMedicationForm({ onSubmit, onCancel }: {
  onSubmit: (draft: NewMedicationDraft) => void
  onCancel: () => void
}) {
  const [kind, setKind] = useState<MedicationKind>('medication')
  const [name, setName] = useState('')
  const [dose, setDose] = useState('')
  const [instructions, setInstructions] = useState('')
  const [notes, setNotes] = useState('')

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const composerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { medications, supplements } = useMedicationCatalog()
  const catalog = kind === 'medication' ? medications : supplements

  useEffect(() => { inputRef.current?.focus() }, [])

  // Switching kind swaps the catalog underneath the field; drop a stale
  // selection rather than leaving a supplement name under "Medication".
  useEffect(() => { setDropdownOpen(false); setHighlightedIndex(0) }, [kind])

  const filtered = useMemo(() => {
    const query = name.trim().toLowerCase()
    if (!query) return catalog.slice(0, 50)
    return catalog.filter(n => n.toLowerCase().includes(query)).slice(0, 50)
  }, [name, catalog])

  useEffect(() => { setHighlightedIndex(0) }, [filtered.length])

  // Close dropdown on outside mousedown. Cleanup listener on unmount/close.
  useEffect(() => {
    if (!dropdownOpen) return
    const onDocMouseDown = (e: MouseEvent) => {
      if (composerRef.current && !composerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [dropdownOpen])

  const choose = (value: string) => {
    setName(value)
    setDropdownOpen(false)
  }

  const canSave = name.trim().length > 0

  const submit = () => {
    if (!canSave) return
    onSubmit({ kind, name: name.trim(), dose: dose.trim(), instructions: instructions.trim(), notes: notes.trim() })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      if (dropdownOpen) setDropdownOpen(false)
      else onCancel()
      return
    }
    if (dropdownOpen && filtered.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex(i => (i + 1) % filtered.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex(i => (i - 1 + filtered.length) % filtered.length)
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        choose(filtered[highlightedIndex])
        return
      }
    }
    // Free text: Enter commits whatever's typed and moves to the dose field.
    if (e.key === 'Enter' && name.trim()) {
      e.preventDefault()
      setDropdownOpen(false)
    }
  }

  // Package has no multiline field — keep a native textarea on the shared
  // Input chrome (cloud border, sm radius, xs type) so the pair reads as one.
  const fieldTextarea: React.CSSProperties = {
    width: '100%',
    height: 60,
    padding: '8px 10px',
    border: '1px solid var(--cloud)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-xs-size)',
    fontFamily: 'var(--font-body)',
    color: 'var(--evergreen)',
    background: 'var(--canvas)',
    outline: 'none',
    resize: 'vertical',
    lineHeight: 1.4,
  }

  return (
    <div
      style={{
        border: '1px solid var(--cloud)',
        borderLeft: '3px solid var(--seafoam)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--canvas)',
        padding: '14px 16px 16px',
        marginBottom: 16,
        animation: 'pharmFadeIn 160ms ease-out',
      }}
    >
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color="evergreen"
        mb={3}
        my={0}
      >
        Add medications
      </Text>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {KINDS.map(k => (
          <PressablePill
            key={k.value}
            pressed={kind === k.value}
            onClick={() => setKind(k.value)}
          >
            {k.label}
          </PressablePill>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div ref={composerRef} style={{ flex: '1 1 auto', position: 'relative', minWidth: 0 }}>
          <FormControl w="100%">
            <FormControl.Label>Name</FormControl.Label>
            <Input
              id="pharm-name"
              ref={inputRef}
              value={name}
              placeholder={kind === 'medication' ? 'Search or type a medication…' : 'Search or type a supplement…'}
              onChange={e => { setName(e.target.value); setDropdownOpen(true) }}
              onFocus={() => setDropdownOpen(true)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              w="100%"
              h="32px"
              fontSize="xs"
            />
          </FormControl>
          {dropdownOpen && filtered.length > 0 && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
              background: 'var(--canvas)', border: '1px solid var(--cloud)',
              borderRadius: 'var(--radius-card)',
              boxShadow: '0 4px 12px rgba(4,51,56,0.10)',
              padding: '4px 0', maxHeight: 220, overflowY: 'auto', zIndex: 40,
            }}>
              {filtered.map((option, i) => (
                <Pressable
                  key={option}
                  onMouseEnter={() => setHighlightedIndex(i)}
                  onClick={() => choose(option)}
                  display="block"
                  w="100%"
                  textAlign="left"
                  px={3}
                  py="7px"
                  fontSize="xs"
                  fontFamily="body"
                  color="evergreen"
                  bg={i === highlightedIndex ? 'feather' : 'transparent'}
                >
                  {option}
                </Pressable>
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: '0 0 200px' }}>
          <FormControl w="100%">
            <FormControl.Label>
              Dose{' '}
              <Text as="span" color="bluegrey" fontWeight="normal">(optional)</Text>
            </FormControl.Label>
            <Input
              id="pharm-dose"
              value={dose}
              placeholder="e.g. 20 mg daily"
              onChange={e => setDose(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') onCancel() }}
              autoComplete="off"
              w="100%"
              h="32px"
              fontSize="xs"
            />
          </FormControl>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 12 }}>
        <FormControl flex={1} minW={0}>
          <FormControl.Label>
            Instructions{' '}
            <Text as="span" color="bluegrey" fontWeight="normal">(optional)</Text>
          </FormControl.Label>
          <textarea
            id="pharm-instructions"
            value={instructions}
            placeholder="e.g. Take with food, avoid at bedtime"
            onChange={e => setInstructions(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') onCancel() }}
            style={fieldTextarea}
          />
          <FormControl.HelperText>Shown to the patient alongside the dosage.</FormControl.HelperText>
        </FormControl>

        <FormControl flex={1} minW={0}>
          <FormControl.Label>
            Notes{' '}
            <Text as="span" color="bluegrey" fontWeight="normal">(optional)</Text>
          </FormControl.Label>
          <textarea
            id="pharm-notes"
            value={notes}
            placeholder="e.g. Context for the care team, reason for the change"
            onChange={e => setNotes(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') onCancel() }}
            style={fieldTextarea}
          />
          <FormControl.HelperText>Internal — not shown to the patient.</FormControl.HelperText>
        </FormControl>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button variant="solid" onClick={submit} disabled={!canSave}>Add</Button>
      </div>
    </div>
  )
}
